import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, HostBinding, inject, input, output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NeoUploaderComponent, NeoUploaderModule } from '@neogov/pattern-library/neo-uploader';
import { LoggerProvider } from '@shared';
import { Attachment, AttachmentListItemComponent } from '../attachment-list-item/attachment-list-item.component';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

export interface UploadedFile {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  readonly file: File;
}

export type UploaderFileListAppearance = 'default' | 'attachment-list' | 'queue';

interface FileItem {
  readonly _file: File;
}

interface FileLikeObject {
  readonly name: string;
  readonly size: number;
  readonly type: string;
  readonly rawFile?: File;
}

@Component({
  selector: 'cm-uploader',
  standalone: true,
  imports: [CommonModule, NeoUploaderModule, AttachmentListItemComponent, ButtonComponent, IconComponent],
  templateUrl: './uploader.component.html',
  styleUrl: './uploader.component.less',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploaderComponent),
      multi: true,
    },
  ],
})
export class UploaderComponent implements ControlValueAccessor {
  private readonly _logger = inject(LoggerProvider).createScoped('UploaderComponent');
  public readonly label = input<string>('Upload Files');
  public readonly hint = input<string>('');
  public readonly acceptedFileTypes = input<string[]>([
    '.jpg',
    '.jpeg',
    '.png',
    '.pdf',
    '.doc',
    '.docx',
    '.mp3',
    '.wav',
    '.m4a',
    '.aac',
    '.mp4',
    '.ogg',
    '.amr',
    '.xlsx',
    '.xls',
  ]);
  public readonly maxFileSize = input<number>(300); // MB
  public readonly maxFiles = input<number>(5);
  public readonly autId = input<string>('uploader');
  public readonly disabled = input<boolean>(false);
  public readonly uploaderStyle = input<'old' | 'new' | 'hidden' | 'custom'>('new');
  public readonly hideFileContainer = input<boolean>(false);

  /** Visual treatment of the file list. @default 'default' */
  public readonly fileListAppearance = input<UploaderFileListAppearance>('default');

  /** Files to list when `fileListAppearance` is `'attachment-list'`. */
  public readonly attachments = input<Attachment[]>([]);

  /** Whether listed attachments can be deleted. @default true */
  public readonly canDeleteAttachments = input<boolean>(true);

  /**
   * Read-only: files can be viewed but not added or removed. Hides the drop area
   * and the delete action, leaving just the list (or `emptyText`).
   * @default false
   */
  public readonly readOnly = input<boolean>(false);

  /** Id of the attachment currently being deleted, to show its spinner. */
  public readonly deletingAttachmentId = input<string | null>(null);

  /** Message shown when there are no attachments. Empty renders nothing. */
  public readonly emptyText = input<string>('');

  public readonly filesSelected = output<UploadedFile[]>();
  public readonly fileRemoved = output<UploadedFile>();

  /** Emitted when the user downloads a listed attachment. */
  public readonly attachmentDownload = output<Attachment>();

  /** Emitted when the user deletes a listed attachment. */
  public readonly attachmentDelete = output<Attachment>();

  /** Whether the component renders the attachment list itself. */
  public readonly showsAttachmentList = computed(
    () => this.fileListAppearance() === 'attachment-list',
  );

  /** Whether the component renders its own pending-selection queue. */
  public readonly showsQueue = computed(() => this.fileListAppearance() === 'queue');

  /**
   * neo-uploader's own queue is always hidden when we render our own list
   * (attachment-list or queue), otherwise the same file shows up twice.
   */
  public readonly isFileContainerHidden = computed(
    () => this.hideFileContainer() || this.showsAttachmentList() || this.showsQueue(),
  );

  /**
   * In the attachment-list appearance the drop area only serves the empty state:
   * once there is at least one attachment the cards take over and the drop area is
   * hidden, so files are added from the consumer's own trigger (`openFilePicker()`).
   * A read-only uploader never shows it.
   */
  public readonly effectiveUploaderStyle = computed(() => {
    const hideDropArea =
      this.readOnly() || (this.showsAttachmentList() && this.attachments().length > 0);
    return hideDropArea ? 'hidden' : this.uploaderStyle();
  });

  /** Delete is never offered on a read-only list. */
  public readonly showsDeleteAction = computed(
    () => this.canDeleteAttachments() && !this.readOnly(),
  );

  /**
   * The empty message and the drop area are mutually exclusive: when the drop area
   * is visible it already tells the user the list is empty.
   */
  public readonly showsEmptyText = computed(
    () =>
      this.showsAttachmentList() &&
      this.attachments().length === 0 &&
      !!this.emptyText() &&
      this.effectiveUploaderStyle() === 'hidden',
  );

  /** Latest validation/upload error, surfaced in a `role="alert"` live region. */
  public readonly errorMessage = signal<string>('');

  private readonly _files = signal<UploadedFile[]>([]);

  /** Pending files the user has selected. Rendered by the built-in list when appearance is `queue`. */
  public readonly queuedFiles = this._files.asReadonly();
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- ControlValueAccessor initialization
  private _onChange: (value: UploadedFile[]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- ControlValueAccessor initialization
  private _onTouched: () => void = () => {};
  private _disabled = false;

  /**
   * Reference to the underlying neo-uploader component for queue management
   */
  private readonly _neoUploader = viewChild<NeoUploaderComponent>('neoUploader');

  /**
   * Effective disabled state combining input disabled and ControlValueAccessor disabled
   */
  public readonly isDisabled = computed(() => this.disabled() || this._disabled);

  @HostBinding('style.--max-files')
  public get maxFilesVar(): string {
    return `"Maximum number of files: ${this.maxFiles()}"`;
  }

  public writeValue(value: UploadedFile[]): void {
    this._files.set(value || []);
  }

  public registerOnChange(fn: (value: UploadedFile[]) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  public onFileAddedToQueue(fileItem: FileItem): void {
    const file = fileItem._file;
    const uploadedFile: UploadedFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    };

    this.errorMessage.set('');
    this._files.update((files) => [...files, uploadedFile]);
    this._onChange(this._files());
    this._onTouched();
    this.filesSelected.emit([uploadedFile]);
  }

  public onExtensionError(file: FileLikeObject): void {
    const name = file?.name ? `"${file.name}" ` : '';
    this.errorMessage.set(
      `${name}has an unsupported file type. Allowed types: ${this.acceptedFileTypes().join(', ')}.`,
    );
  }

  public onSizeError(file: FileLikeObject): void {
    const name = file?.name ? `"${file.name}" ` : '';
    this.errorMessage.set(`${name}exceeds the maximum allowed size of ${this.maxFileSize()}MB.`);
  }

  public onUploadFail(): void {
    this.errorMessage.set('The file could not be uploaded. Please try again.');
  }

  public onFileRemovedFromQueue(fileLikeObject: FileLikeObject): void {
    const uploadedFile: UploadedFile = {
      name: fileLikeObject.name,
      size: fileLikeObject.size,
      type: fileLikeObject.type,
      file: fileLikeObject.rawFile || (fileLikeObject as unknown as File),
    };

    this._files.update((files) => files.filter((f) => f.name !== uploadedFile.name));
    this._onChange(this._files());
    this._onTouched();
    this.fileRemoved.emit(uploadedFile);
  }

  public openFilePicker(): void {
    this._neoUploader()?.openFileSelectDialog();
  }

  public removeFile(name: string): void {
    this._files.update((files) => files.filter((f) => f.name !== name));
    this._onChange(this._files());
    this._onTouched();

    const uploaderComponent = this._neoUploader();
    if (uploaderComponent) {
      try {
        const queue = (uploaderComponent as unknown as { uploader: { queue: { _file: File; }[]; removeFromQueue: (item: unknown) => void } }).uploader.queue;
        const item = queue.find((q) => q._file.name === name);
        if (item) {
          (uploaderComponent as unknown as { uploader: { removeFromQueue: (item: unknown) => void } }).uploader.removeFromQueue(item);
        }
      } catch (error) {
        this._logger.warn('Failed to remove file from neo-uploader queue', { error });
      }
    }
  }

  public onQueueRemove(file: UploadedFile): void {
    this.removeFile(file.name);
    this.fileRemoved.emit(file);
  }

  public clearQueue(): void {
    this._files.set([]);
    this._onChange(this._files());

    const uploaderComponent = this._neoUploader();
    if (uploaderComponent) {
      try {
        uploaderComponent.clearFilesQueue(true);
      } catch (error) {
        this._logger.warn('Failed to clear neo-uploader queue', { error });
      }
    }
  }
}
